import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatDateTime, formatErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasAnyRole } from "@/lib/perms";
import { PageHeader, Surface, Empty, TableWrap } from "@/components/app/Primitives";
import EmployeePicker from "@/components/app/EmployeePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trophy, Medal, Sparkles, Gift, Trash2, Award, Check, X } from "lucide-react";

const CAT_LABEL = {
    teamwork: "🤝 Teamwork", excellence: "⭐ Excellence", innovation: "💡 Innovation",
    customer_service: "🎧 Customer Service", leadership: "🧭 Leadership", above_and_beyond: "🚀 Above & Beyond",
};

/* HR-only management: redemption queue, catalog items, earning activities,
 * and awarding activity points to employees. */
function ExchangeAdmin() {
    const [catalog, setCatalog] = useState({ items: [], activities: [] });
    const [queue, setQueue] = useState(null);
    const [itemForm, setItemForm] = useState({ name: "", points_cost: "", description: "" });
    const [actForm, setActForm] = useState({ name: "", points: "", description: "" });
    const [award, setAward] = useState({ employee_id: "", activity_id: "", note: "" });

    const load = () => {
        api.get("/rewards/admin/catalog").then((r) => setCatalog(r.data)).catch(() => {});
        api.get("/rewards/admin/redemptions").then((r) => setQueue(r.data)).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const err = (e) => toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed");
    const addItem = () => api.post("/rewards/admin/items", { ...itemForm, points_cost: Number(itemForm.points_cost) }).then(() => { toast.success("Reward added"); setItemForm({ name: "", points_cost: "", description: "" }); load(); }).catch(err);
    const delItem = (id) => window.confirm("Remove this reward from the catalog?") && api.delete(`/rewards/admin/items/${id}`).then(load).catch(err);
    const addAct = () => api.post("/rewards/admin/activities", { ...actForm, points: Number(actForm.points) }).then(() => { toast.success("Activity added"); setActForm({ name: "", points: "", description: "" }); load(); }).catch(err);
    const delAct = (id) => window.confirm("Remove this earning activity?") && api.delete(`/rewards/admin/activities/${id}`).then(load).catch(err);
    const giveAward = () => {
        if (!award.employee_id || !award.activity_id) return toast.error("Pick an employee and an activity");
        api.post("/rewards/admin/award", award).then((r) => { toast.success(`Awarded ${r.data.points} pts to ${r.data.employee_name} 🎉`); setAward({ employee_id: "", activity_id: "", note: "" }); }).catch(err);
    };
    const decide = (r, status) => {
        let note = "";
        if (status === "rejected") { note = window.prompt("Reason?") || ""; if (!note) return; }
        api.post(`/rewards/admin/redemptions/${r.id}/status`, { status, note }).then(() => { toast.success(status === "fulfilled" ? "Marked fulfilled — hand the reward over 🎁" : "Rejected (points refunded)"); load(); }).catch(err);
    };

    return (
        <Surface className="p-4 mt-6 border-primary/30" data-testid="exchange-admin">
            <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Gift className="size-4 text-primary" /> HR — Rewards Exchange Management</h3>
            <div className="grid gap-4 lg:grid-cols-2">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Redemption queue {queue ? `(${queue.pending} pending)` : ""}</div>
                    {!queue?.items?.length && <div className="text-xs text-muted-foreground border border-border rounded-md p-3">No redemptions yet.</div>}
                    {!!queue?.items?.length && (
                        <TableWrap>
                            <thead><tr><th>Employee</th><th>Reward</th><th className="num">Pts</th><th>Status</th><th>Actions</th></tr></thead>
                            <tbody>
                                {queue.items.slice(0, 10).map((r) => (
                                    <tr key={r.id}>
                                        <td className="text-sm">{r.employee_name}</td>
                                        <td className="text-sm">{r.item_name}</td>
                                        <td className="num font-mono">{r.points_cost}</td>
                                        <td><span className={`badge ${r.status === "pending" ? "badge-pending" : r.status === "fulfilled" ? "badge-approved" : "badge-destructive"}`}>{r.status}</span></td>
                                        <td>
                                            {r.status === "pending" && (
                                                <div className="flex gap-1">
                                                    <button className="h-7 px-2 text-xs rounded-md bg-emerald-600 text-white flex items-center gap-1" onClick={() => decide(r, "fulfilled")} data-testid={`fulfil-${r.id}`}><Check className="size-3" /> Fulfil</button>
                                                    <button className="h-7 px-2 text-xs rounded-md bg-destructive text-white flex items-center gap-1" onClick={() => decide(r, "rejected")}><X className="size-3" /></button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </TableWrap>
                    )}

                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-4 mb-1.5 flex items-center gap-1"><Award className="size-3" /> Award activity points</div>
                    <div className="space-y-2 border border-border rounded-md p-3">
                        <EmployeePicker value={award.employee_id} onChange={(v) => setAward({ ...award, employee_id: v })} testId="award-employee" />
                        <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={award.activity_id} onChange={(e) => setAward({ ...award, activity_id: e.target.value })} data-testid="award-activity">
                            <option value="">Pick an activity…</option>
                            {catalog.activities.map((a) => <option key={a.id} value={a.id}>{a.name} · +{a.points} pts</option>)}
                        </select>
                        <Input placeholder="Note (e.g., June 2026 — no lates)" value={award.note} onChange={(e) => setAward({ ...award, note: e.target.value })} />
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm w-full" onClick={giveAward} data-testid="award-btn">Award points</button>
                    </div>
                </div>

                <div className="space-y-4">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Catalog items</div>
                        <div className="space-y-1.5">
                            {catalog.items.map((it) => (
                                <div key={it.id} className="flex items-center justify-between border border-border rounded-md px-2.5 py-1.5 text-sm">
                                    <span>{it.name} <span className="font-mono text-xs text-primary font-bold">{it.points_cost} pts</span></span>
                                    <button className="text-muted-foreground hover:text-destructive" onClick={() => delItem(it.id)}><Trash2 className="size-3.5" /></button>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-1.5 mt-2">
                            <Input placeholder="New reward name" value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} data-testid="new-item-name" />
                            <Input placeholder="Pts" type="number" className="w-20" value={itemForm.points_cost} onChange={(e) => setItemForm({ ...itemForm, points_cost: e.target.value })} data-testid="new-item-cost" />
                            <button className="h-9 px-3 rounded-md bg-primary text-white text-sm shrink-0" onClick={addItem} data-testid="add-item-btn"><Plus className="size-4" /></button>
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Point-earning activities</div>
                        <div className="space-y-1.5">
                            {catalog.activities.map((a) => (
                                <div key={a.id} className="flex items-center justify-between border border-border rounded-md px-2.5 py-1.5 text-sm">
                                    <span>{a.name} <span className="badge badge-approved font-mono text-[10px]">+{a.points}</span></span>
                                    <button className="text-muted-foreground hover:text-destructive" onClick={() => delAct(a.id)}><Trash2 className="size-3.5" /></button>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-1.5 mt-2">
                            <Input placeholder="New activity (e.g., Perfect attendance)" value={actForm.name} onChange={(e) => setActForm({ ...actForm, name: e.target.value })} data-testid="new-act-name" />
                            <Input placeholder="Pts" type="number" className="w-20" value={actForm.points} onChange={(e) => setActForm({ ...actForm, points: e.target.value })} data-testid="new-act-pts" />
                            <button className="h-9 px-3 rounded-md bg-primary text-white text-sm shrink-0" onClick={addAct} data-testid="add-act-btn"><Plus className="size-4" /></button>
                        </div>
                    </div>
                </div>
            </div>
        </Surface>
    );
}

export default function Rewards() {
    const navigate = useNavigate();
    const { user, activeCompanyId } = useAuth();
    const isHR = hasAnyRole(user, activeCompanyId, ["hr_admin", "hr_staff", "hr_manager"]);
    const [feed, setFeed] = useState([]);
    const [cats, setCats] = useState({});
    const [board, setBoard] = useState(null);
    const [me, setMe] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ to_employee_id: "", category: "teamwork", message: "" });
    const [tab, setTab] = useState("monthly");

    const load = () => {
        api.get("/rewards/feed").then((r) => { setFeed(r.data.items || []); setCats(r.data.categories || {}); }).catch(() => {});
        api.get("/rewards/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
        api.get("/rewards/me").then((r) => setMe(r.data)).catch(() => setMe(null));
        api.get("/hris/employees").then((r) => setEmployees(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const give = async () => {
        try {
            await api.post("/rewards/recognize", form);
            toast.success("Recognition sent! 🎉");
            setOpen(false); setForm({ to_employee_id: "", category: "teamwork", message: "" }); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const rows = tab === "monthly" ? board?.monthly : board?.all_time;

    return (
        <div>
            <PageHeader title="Rewards & Recognition" subtitle="Peer-to-peer kudos with points and a leaderboard. Celebrate the wins."
                actions={
                    <div className="flex items-center gap-2">
                    <button className="h-9 px-3 rounded-md border border-primary text-primary text-sm font-medium flex items-center gap-2" onClick={() => navigate("/rewards/exchange")} data-testid="exchange-points-btn"><Gift className="size-4" /> Exchange my points</button>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild><button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="give-kudos-btn"><Plus className="size-4" /> Give Recognition</button></DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Give recognition</DialogTitle></DialogHeader>
                            <div className="space-y-2">
                                <div><Label>To</Label>
                                    <EmployeePicker value={form.to_employee_id} onChange={(v) => setForm({ ...form, to_employee_id: v })} placeholder="Search colleague…" testId="kudos-to" /></div>
                                <div><Label>Category</Label>
                                    <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                        {Object.keys(cats).length ? Object.entries(cats).map(([k, pts]) => <option key={k} value={k}>{CAT_LABEL[k] || k} · {pts} pts</option>) : <option value="teamwork">Teamwork</option>}
                                    </select></div>
                                <div><Label>Message</Label>
                                    <textarea className="w-full h-20 rounded-md border border-border bg-background p-2 text-sm" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What did they do that deserves a shout-out?" data-testid="kudos-msg" /></div>
                            </div>
                            <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={give} data-testid="kudos-send">Send 🎉</button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                    </div>
                } />

            {me && (
                <div className="grid grid-cols-3 gap-3 mb-4 max-w-md">
                    <Surface className="p-3 text-center"><div className="text-xs text-muted-foreground">My points</div><div className="font-display text-xl font-bold">{me.points}</div></Surface>
                    <Surface className="p-3 text-center"><div className="text-xs text-muted-foreground">Received</div><div className="font-display text-xl font-bold">{me.received}</div></Surface>
                    <Surface className="p-3 text-center"><div className="text-xs text-muted-foreground">Given</div><div className="font-display text-xl font-bold">{me.given}</div></Surface>
                </div>
            )}

            <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
                <div>
                    <h3 className="font-display font-semibold mb-2 flex items-center gap-2"><Sparkles className="size-4 text-primary" /> Recognition feed</h3>
                    {!feed.length && <Surface className="p-5"><Empty title="No recognitions yet" subtitle="Be the first to give kudos!" /></Surface>}
                    <div className="space-y-2">
                        {feed.map((r) => (
                            <Surface key={r.id} className="p-3">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="text-sm"><b>{r.from_name}</b> → <b>{r.to_name}</b></div>
                                    <span className="badge badge-approved whitespace-nowrap">+{r.points} pts</span>
                                </div>
                                <div className="text-xs mt-0.5"><span className="badge badge-info">{CAT_LABEL[r.category] || r.category}</span></div>
                                <div className="text-sm text-muted-foreground mt-1.5">“{r.message}”</div>
                                <div className="text-[10px] text-muted-foreground mt-1">{formatDateTime(r.created_at)}</div>
                            </Surface>
                        ))}
                    </div>
                </div>

                <div>
                    <h3 className="font-display font-semibold mb-2 flex items-center gap-2"><Trophy className="size-4 text-amber-500" /> Leaderboard</h3>
                    <Surface className="p-3">
                        <div className="flex gap-1 mb-2">
                            <button onClick={() => setTab("monthly")} className={`flex-1 h-8 rounded-md text-xs font-medium ${tab === "monthly" ? "bg-primary text-white" : "border"}`}>This month</button>
                            <button onClick={() => setTab("all_time")} className={`flex-1 h-8 rounded-md text-xs font-medium ${tab === "all_time" ? "bg-primary text-white" : "border"}`}>All time</button>
                        </div>
                        {(rows || []).map((r, i) => (
                            <div key={r.employee_id} className="flex items-center gap-2 py-1.5 border-b last:border-0">
                                <div className={`size-6 rounded-full grid place-items-center text-xs font-bold ${i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-zinc-200 text-zinc-700" : i === 2 ? "bg-orange-100 text-orange-800" : "bg-muted text-muted-foreground"}`}>{i + 1}</div>
                                <div className="flex-1 text-sm">{r.name}</div>
                                <div className="text-sm font-semibold tabular-nums">{r.points}</div>
                            </div>
                        ))}
                        {!rows?.length && <div className="text-xs text-muted-foreground py-2">No points {tab === "monthly" ? "this month" : "yet"}.</div>}
                    </Surface>
                </div>
            </div>

            {isHR && <ExchangeAdmin />}
        </div>
    );
}
